import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';
import { UpdateTaskDueDate } from '../model/update-task-due-date';

@Injectable({
  providedIn: 'root'
})
export class TaskService implements OnDestroy
{
  public listPriorities: Array<{ id: string; text: string; value: number }> = [
    { id: 'Low', text: 'Low', value: 30 },
    { id: 'Medium', text: 'Medium', value: 50 },
    { id: 'High', text: 'High', value: 70 },
    { id: 'Urgent', text: 'Urgent', value: 90 }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get all tasks
   */
  public getTasks(): Observable<any[]>
  {
    return this.http.get<any[]>('/tasks');
  }

  /**
   * Get a task
   */
  public getTask(taskId: string, directFetch: boolean = false): Observable<any>
  {
    const directFetchQueryString = directFetch ? `?directFetch=true` : '';
    return this.http.get<any>(`/tasks/${taskId}${directFetchQueryString}`);
  }

  /**
   * Get task history
   */
  public getTaskHistory(taskId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/${taskId}/events`);
  }

  /**
   * Get task details
   */
  public getTaskDetails(taskId: number): Observable<any>
  {
    return this.http.get<any>(`/tasks/${taskId}/details`);
  }

  /**
   * Get task outcomes
   */
  public getTaskOutcomes(bpmId: string, taskDefinitionKey: string, taskFormKey, taskId: string): Observable<any>
  {
    return this.http.get<any>(`/tasks/outcomes?bpmId=${bpmId}&taskDefinitionKey=${taskDefinitionKey}
    &taskFormKey=${taskFormKey}&taskId=${taskId}`);
  }

  /**
   * Updates a task.
   *
   * @param taskId Task ID
   * @param task Task to update.
   */
  public updateTaskDetails(taskId, taskDetails): Observable<any>
  {
    return this.http.put<any>(`/tasks/${taskId}/details`, taskDetails);
  }

  /**
   * Get all tasks for member
   */
  public getTasksForMember(memberId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/member/${memberId}`);
  }

  /**
   * Get all tasks for member event
   */
  public getTasksForMemberEvent(memberId: number, eventId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/member/${memberId}/event/${eventId}`);
  }

  /**
   * Get all tasks for assginee
   */
  public getTasksForAssignee(username: string): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/assignee/${username}`);
  }

  /**
   * Get all summary tasks for assginee
   */
  public getTasksSummaryForAssignee(username: string): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/assignee/${username}/summary`);
  }

  /**
   * Get all task definitions for key
   */
  public getTaskDefinitionForKey(procDefKey: string): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/definition/${procDefKey}`);
  }

  /**
   * Updates a task.
   *
   * @param taskId Task ID
   * @param task Task to update.
   */
  public updateTask(taskId, task): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}`, task);
  }

  /**
   * Update the task priority.
   *
   * @param taskId Task ID.
   * @param priority New priority.
   */
  public updatePriority(taskId, priority): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}/priority`, priority);
  }

  /**
   * Assign the task to a user.
   *
   * @param taskId Task ID.
   * @param userName Assignee user name.
   */
  public assignTask(taskId, userName): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}/assign`, userName);
  }

  /**
   * Unassign the task to a user.
   *
   * @param taskId Task ID.
   */
  public unAssignTask(taskId): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}/unassign`, null);
  }

  /**
   * Completes a task.
   *
   * @param taskId Task ID
   * @param task The task to complete.
   */
  public completeTask(taskId: string, task: any): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}/complete`, task);
  }

  public forceCloseTask(taskId: string, reason: any): Observable<any[]>
  {
    return this.http.put<any>(`/tasks/${taskId}/force/close`, reason);
  }

  /**
   * Save a Task.
   *
   * @param task Task to save.
   */
  public saveTask(task: any): Observable<any>
  {
    return this.http.post<any>('/tasks', task);
  }

  /**
   * Updates the due date of a task.
   *
   * @param taskId Task ID.
   * @param dueDate New due date of the task.
   */
  public updateTaskDueDate(taskId: number, dueDate: Date, reasonReschedule: string): Observable<any>
  {
    const updateTask: UpdateTaskDueDate = new UpdateTaskDueDate();
    updateTask.dueDate = dueDate;
    updateTask.reasonReschedule = reasonReschedule;
    return this.http.put<any>(`/tasks/${taskId}/duedate`, updateTask);
  }

  /**
   * Get tasks by channels and member.
   */
  public getTasksByMemberAndChannels(channels: string, memberId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/dashboard/${memberId}/${channels}`);
  }

  /**
   * Get all tasks for follow-up tasks
   */
  public getTasksForFollowUpTask(memberId: number, taskId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/member/${memberId}/task/${taskId}`);
  }

  /**
   * Get task related to
   */
  public getTaskRelatedTo(taskId: string): Observable<any>
  {
    return this.http.get<any>(`/tasks/${taskId}/related`);
  }

  /**
   * Get all tasks for follow-up tasks
   */
  public getTasksDueTodayAndOverdue(): Observable<any[]>
  {
    return this.http.get<any[]>(`/tasks/dashboard/notice`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.translate.get(['LOW', 'MEDIUM', 'HIGH', 'URGENT']).subscribe({
        next: (res) =>
        {
          let index = 0;
          for (const key in res)
          {
            if ({}.hasOwnProperty.call(res, key))
            {
              this.listPriorities[index].text = res[key];
              index++;
            }
          }
        }
      });
    });
  }
}
